import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useLinkToken } from "@/pages/ResetPassword";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthShell } from "@/pages/Login";
import { TermsConsent, TERMS_NEEDED } from "@/components/Legal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Check, X } from "lucide-react";
import { roleLabel, homeFor } from "@/config";
import { InviteTerms, AgreeBox } from "@/components/HiringTerms";
import { shortDate } from "@/components/common";

// An investor's invite (FEATURE_INVESTORS): what they'll see, following the founders' switches (backend/investor_routes.py)
function InvestorSees({ sharing, company }) {
  const items = [
    [true, "Progress: objectives and change report counts"],
    [sharing?.money, sharing?.hiring_costs ? "Money per objective, with hiring costs" : "Money per objective"],
    [sharing?.plan, "Plan, AI credits and invoices"],
    [sharing?.reliability, "Reliability: outages and errors"],
    [sharing?.delivery, "Delivery times per objective"],
    [true, "Team names, titles and kinds of work"],
  ];
  return (
    <div className="flex flex-col gap-2 p-3.5 rounded-lg border" data-testid="invite-investor-sees">
      <span className="text-xs uppercase tracking-[0.09em] text-muted-foreground">What you'll see</span>
      <ul className="m-0 p-0 list-none flex flex-col gap-1.5 text-sm">
        {items.filter(([on]) => on).map(([, label]) => (
          <li key={label} className="flex items-start gap-2"><Check className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gr-success" aria-hidden="true" />{label}</li>
        ))}
        <li className="flex items-start gap-2 text-muted-foreground"><X className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
          Never sessions, change report summaries or the founders' messages with the team.</li>
      </ul>
      <p className="m-0 text-sm text-muted-foreground">View only. You can write to {company ? `${company}'s` : "the"} founders in Messages, and get a summary email on Mondays.</p>
    </div>
  );
}

export default function AcceptInvite() {
  // new links carry the token after "#"; links made before 2026-09-30 had it in the path
  const hashToken = useLinkToken();
  const { token: pathToken } = useParams();
  const token = hashToken || pathToken;
  useEffect(() => {  // an older link had the token in the path: take it out of the address bar too
    if (pathToken) window.history.replaceState(null, "", "/accept-invite");
  }, [pathToken]);
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [f, setF] = useState({ name: "", password: "" });
  const [agreed, setAgreed] = useState(false);
  const [hired, setHired] = useState(false);  // "I agree to these hiring terms" (an independent professional's task)
  const [loading, setLoading] = useState(false);
  const { setSession } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) { setErr("This invite link is incomplete. Open it from the email again."); return; }
    api.post("/auth/invite", { token }).then(({ data }) => setInfo(data)).catch((e) => setErr(apiErr(e)));
  }, [token]);

  const needsHire = info?.task?.engagement === "independent";
  const submit = async (e) => {
    e.preventDefault();
    if (!agreed) { toast.error(TERMS_NEEDED); return; }
    if (needsHire && !hired) { toast.error("Tick \u201cI agree to these hiring terms\u201d to join."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { token, ...f, accept_terms: agreed, agree_terms: hired });
      setSession(data);
      toast.success(info.role === "viewer" ? `Welcome to ${info.company_name}'s investor view` : info.returning ? "Welcome back to the team" : "Welcome to the team");
      navigate(homeFor(data.user.role));
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally {
      setLoading(false);
    }
  };

  if (err) return (
    <AuthShell title="Invite invalid" subtitle={err}>
      <Link to="/login" className="text-foreground underline text-sm">Back to sign in</Link>
    </AuthShell>
  );
  if (!info) return <AuthShell title="Loading invite" subtitle="Please wait"><Loader2 className="w-5 h-5 animate-spin" /></AuthShell>;

  // someone the company finished and removed comes back on their own account: its password, not a new one
  if (info.returning) return (
    <AuthShell title={`Join ${info.company_name} again`} subtitle={`Invited as ${(info.title || roleLabel(info.role)).toLowerCase()} · ${info.email}`}>
      <form onSubmit={submit} className="space-y-4">
        {/* the hiring terms they agree to (backend/assignment_routes.py), and who invited them */}
        {info.invited_by && <p className="text-sm text-muted-foreground">{info.invited_by} invited you{info.invited_at ? ` on ${shortDate(info.invited_at)}` : ""}.</p>}
        {info.task && <InviteTerms task={info.task} />}
        {info.role === "viewer" && <InvestorSees sharing={info.sharing} company={info.company_name} />}
        {info.task?.engagement === "employee" && <p className="text-sm text-muted-foreground">Your sessions are private: {info.company_name} sees results and spend, never your conversations.</p>}
        <input type="email" value={info.email} readOnly autoComplete="username" hidden />
        <div className="space-y-1.5"><Label htmlFor="invite-password">Your password</Label>
          <Input id="invite-password" data-testid="invite-password" type="password" autoComplete="current-password"
            value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
          <p className="text-sm text-muted-foreground">You worked with {info.company_name} before: use your account's password.
            Forgot it? <Link to="/forgot-password" className="text-foreground underline underline-offset-2">Reset it</Link>, then open this link again.</p></div>
        {needsHire && <AgreeBox task={info.task} checked={hired} onChange={setHired} />}
        <TermsConsent id="invite-terms" checked={agreed} onChange={setAgreed} />
        <Button type="submit" className="w-full" disabled={loading} data-testid="invite-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Join again
        </Button>
      </form>
    </AuthShell>
  );

  return (
    <AuthShell title={`Join ${info.company_name}`} subtitle={`Invited as ${(info.title || roleLabel(info.role)).toLowerCase()} · ${info.email}`}>
      <form onSubmit={submit} className="space-y-4">
        {/* the hiring terms they agree to (backend/assignment_routes.py), and who invited them */}
        {info.invited_by && <p className="text-sm text-muted-foreground">{info.invited_by} invited you{info.invited_at ? ` on ${shortDate(info.invited_at)}` : ""}.</p>}
        {info.task && <InviteTerms task={info.task} />}
        {info.role === "viewer" && <InvestorSees sharing={info.sharing} company={info.company_name} />}
        {info.task?.engagement === "employee" && <p className="text-sm text-muted-foreground">Your sessions are private: {info.company_name} sees results and spend, never your conversations.</p>}
        {/* the account's username, so a password manager saves the new password under the right email */}
        <input type="email" value={info.email} readOnly autoComplete="username" hidden />
        <div className="space-y-1.5"><Label htmlFor="invite-name">Your name</Label>
          <Input id="invite-name" data-testid="invite-name" autoComplete="name" value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div className="space-y-1.5"><Label htmlFor="invite-password">Set a password</Label>
          <Input id="invite-password" data-testid="invite-password" type="password" autoComplete="new-password"
            value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
        {needsHire && <AgreeBox task={info.task} checked={hired} onChange={setHired} />}
        <TermsConsent id="invite-terms" checked={agreed} onChange={setAgreed} />
        <Button type="submit" className="w-full" disabled={loading} data-testid="invite-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {info.role === "viewer" ? `Join ${info.company_name}` : "Join team"}
        </Button>
      </form>
    </AuthShell>
  );
}
